import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  QueryStateBoundary,
  Tabs,
  TabList,
  TabTrigger,
  TabContent,
} from "@/components/ui";
import { useContactOverview } from "@/api/hooks/useContactOverview";
import { useVoice } from "@/hooks/useVoice";
import { toastError } from "@/components/ui/Toast";
import {
  CONTACT_KIND_LABELS,
  type Contact,
} from "@/api/types/contact";
import type {
  OverviewEvent,
  OverviewInquiry,
  OverviewInvoice,
  OverviewOutreach,
  OverviewPayment,
  OverviewQuote,
  OverviewReview,
  TimelineRow,
} from "@/api/types/contactOverview";
import { STAGE_LABELS } from "@/api/types/inquiry";
import { PACKAGE_CODE_LABELS } from "@/api/types/package";
import { QUOTE_STATUS_BADGE_VARIANT, QUOTE_STATUS_LABELS } from "@/api/types/quote";
import { EVENT_STATUS_BADGE_VARIANT, EVENT_STATUS_LABELS } from "@/api/types/event";
import {
  INVOICE_KIND_BADGE_VARIANT,
  INVOICE_KIND_LABELS,
  INVOICE_STATUS_BADGE_VARIANT,
  INVOICE_STATUS_LABELS,
} from "@/api/types/invoice";
import { PAYMENT_METHOD_LABELS } from "@/api/types/payment";
import { REVIEW_PLATFORM_BADGE_VARIANT, REVIEW_PLATFORM_LABELS } from "@/api/types/review";
import {
  STATUS_LABELS as OUTREACH_STATUS_LABELS,
  TIER_BADGE_VARIANT,
  TIER_LABELS,
} from "@/api/types/outreach";
import { centsToUsd } from "@/lib/money";
import { formatDateOnly, formatDateTime, relativeTime, timelineIcon } from "./contactTimelineFormat";
import { ContactFormDialog } from "./ContactFormDialog";
import { LogInteractionDialog } from "./LogInteractionDialog";
import { ComposeEmailDialog } from "./ComposeEmailDialog";
import { ComposeSmsDialog } from "./ComposeSmsDialog";
import { CallDialog } from "./CallDialog";

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: overview, isLoading, error, refetch } = useContactOverview(id);
  const [editOpen, setEditOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [tab, setTab] = useState("timeline");
  const voice = useVoice();

  function handleCall(contactId: string, phone: string) {
    if (!voice.enabled) {
      toastError(
        "In-app calling isn't set up yet",
        "Twilio isn't configured on the server yet — ask an admin to finish setup.",
      );
      return;
    }
    setCallOpen(true);
    voice.call(phone, contactId);
  }

  function handleCallDialogClose() {
    setCallOpen(false);
    void refetch();
  }

  const sortedTimeline = useMemo(() => {
    const rows = overview?.timeline ?? [];
    // Defensive: the contract says newest-first already, but a stable
    // client-side sort costs nothing and guards against a backend revision
    // that returns insertion order instead.
    return [...rows].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [overview?.timeline]);

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6 sm:p-8">
      <Button variant="link" size="sm" onClick={() => navigate("/contacts")}>
        ← Back to contacts
      </Button>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {overview && (
          <>
            <ContactHeaderCard
              contact={overview.contact}
              onEdit={() => setEditOpen(true)}
              onSendEmail={() => setEmailOpen(true)}
              onSendText={() => setSmsOpen(true)}
              onCall={() => handleCall(overview.contact.id, overview.contact.phone ?? "")}
              voiceEnabled={voice.enabled}
              voiceTokenLoading={voice.tokenLoading}
            />

            <Card className="p-0">
              <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
                <Tabs value={tab} onValueChange={setTab} className="overflow-x-auto">
                  <TabList className="border-b-0">
                    <TabTrigger value="timeline">History</TabTrigger>
                    <TabTrigger value="inquiries">
                      Inquiries ({overview.counts.inquiries})
                    </TabTrigger>
                    <TabTrigger value="quotes">Quotes ({overview.counts.quotes})</TabTrigger>
                    <TabTrigger value="events">Events ({overview.counts.events})</TabTrigger>
                    <TabTrigger value="money">
                      Invoices & Payments ({overview.counts.invoices})
                    </TabTrigger>
                    <TabTrigger value="reviews">Reviews ({overview.counts.reviews})</TabTrigger>
                    <TabTrigger value="outreach">
                      Outreach ({overview.counts.outreach})
                    </TabTrigger>
                  </TabList>

                  <div className="p-4">
                    <TabContent value="timeline">
                      <TimelineSection
                        rows={sortedTimeline}
                        onLogInteraction={() => setLogOpen(true)}
                      />
                    </TabContent>
                    <TabContent value="inquiries">
                      <InquiriesTable inquiries={overview.inquiries} />
                    </TabContent>
                    <TabContent value="quotes">
                      <QuotesTable quotes={overview.quotes} />
                    </TabContent>
                    <TabContent value="events">
                      <EventsTable events={overview.events} />
                    </TabContent>
                    <TabContent value="money">
                      <InvoicesPaymentsSection
                        invoices={overview.invoices}
                        payments={overview.payments}
                      />
                    </TabContent>
                    <TabContent value="reviews">
                      <ReviewsTable reviews={overview.reviews} />
                    </TabContent>
                    <TabContent value="outreach">
                      <OutreachSection outreach={overview.outreach} />
                    </TabContent>
                  </div>
                </Tabs>
              </div>
            </Card>

            <ContactFormDialog open={editOpen} onClose={() => setEditOpen(false)} contact={overview.contact} />
            <LogInteractionDialog
              open={logOpen}
              onClose={() => setLogOpen(false)}
              contactId={overview.contact.id}
            />
            <ComposeEmailDialog
              open={emailOpen}
              onClose={() => setEmailOpen(false)}
              contactId={overview.contact.id}
            />
            <ComposeSmsDialog
              open={smsOpen}
              onClose={() => setSmsOpen(false)}
              contactId={overview.contact.id}
            />
            <CallDialog
              open={callOpen}
              contactName={overview.contact.name}
              phone={overview.contact.phone ?? ""}
              voice={voice}
              onClose={handleCallDialogClose}
            />
          </>
        )}
      </QueryStateBoundary>
    </div>
  );
}

// ---- Header ---------------------------------------------------------------

function fullAddress(contact: Contact): string | null {
  const line2 = contact.address_line2?.trim();
  const cityStateZip = [contact.city, contact.state].filter(Boolean).join(", ");
  const cityStateZipFull = [cityStateZip, contact.postal_code].filter(Boolean).join(" ");
  const lines = [contact.address_line1, line2, cityStateZipFull].filter(
    (line): line is string => !!line && line.trim() !== "",
  );
  return lines.length > 0 ? lines.join(", ") : null;
}

function ContactHeaderCard({
  contact,
  onEdit,
  onSendEmail,
  onSendText,
  onCall,
  voiceEnabled,
  voiceTokenLoading,
}: {
  contact: Contact;
  onEdit: () => void;
  onSendEmail: () => void;
  onSendText: () => void;
  onCall: () => void;
  voiceEnabled: boolean;
  voiceTokenLoading: boolean;
}) {
  const address = fullAddress(contact);
  const hasEmail = !!contact.email;
  const hasPhone = !!contact.phone;
  // In-app calling (Phase 15) replaces the old click-to-call PSTN bridge —
  // no fallback. Disabled + a tooltip explaining why, rather than
  // resurrecting the old bridge, when either the contact has no phone or
  // Twilio isn't configured server-side yet.
  const callDisabled = !hasPhone || voiceTokenLoading || !voiceEnabled;
  const callTitle = !hasPhone
    ? "Contact has no phone on file"
    : voiceTokenLoading
      ? undefined
      : !voiceEnabled
        ? "In-app calling isn't set up yet"
        : undefined;

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>{contact.name}</CardTitle>
            <Badge variant="secondary">{CONTACT_KIND_LABELS[contact.kind]}</Badge>
            <Badge variant={contact.warm === "warm" ? "success" : "default"}>
              {contact.warm === "warm" ? "Warm" : "Cold"}
            </Badge>
            {contact.email_consent && (
              <Badge variant="success" size="sm">
                Email OK
              </Badge>
            )}
            {contact.sms_consent && (
              <Badge variant="success" size="sm">
                SMS OK
              </Badge>
            )}
          </div>
          {contact.company && <p className="mt-1 text-sm text-text-secondary">{contact.company}</p>}
        </div>
        <div className="flex items-center gap-2">
          {/* Wrapped in a span: the Button is pointer-events-none while
              disabled, which would otherwise swallow the hover needed for
              the native title tooltip to appear. */}
          <span title={hasEmail ? undefined : "Contact has no email on file"}>
            <Button variant="secondary" size="sm" onClick={onSendEmail} disabled={!hasEmail}>
              Send email
            </Button>
          </span>
          <span title={hasPhone ? undefined : "Contact has no phone on file"}>
            <Button variant="secondary" size="sm" onClick={onSendText} disabled={!hasPhone}>
              Text
            </Button>
          </span>
          <span title={callTitle}>
            <Button variant="secondary" size="sm" onClick={onCall} disabled={callDisabled}>
              Call
            </Button>
          </span>
          <Button variant="secondary" size="sm" onClick={onEdit}>
            Edit
          </Button>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
        <InfoRow label="Phone">
          {contact.phone ? (
            <a href={`tel:${contact.phone}`} className="text-primary hover:underline">
              {contact.phone}
            </a>
          ) : (
            <span className="text-text-muted">—</span>
          )}
        </InfoRow>
        <InfoRow label="Email">
          {contact.email ? (
            <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
              {contact.email}
            </a>
          ) : (
            <span className="text-text-muted">—</span>
          )}
        </InfoRow>
        <InfoRow label="Instagram">
          {contact.instagram ? (
            <a
              href={
                contact.instagram.startsWith("http")
                  ? contact.instagram
                  : `https://instagram.com/${contact.instagram.replace(/^@/, "")}`
              }
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              {contact.instagram}
            </a>
          ) : (
            <span className="text-text-muted">—</span>
          )}
        </InfoRow>
        <InfoRow label="Address">
          {address ? (
            <span className="text-text-primary" data-testid="contact-address">
              {address}
            </span>
          ) : (
            <span className="text-text-muted">No address on file</span>
          )}
        </InfoRow>
      </CardContent>
    </Card>
  );
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-text-muted">{label}</p>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

// ---- Timeline ---------------------------------------------------------------

function TimelineSection({
  rows,
  onLogInteraction,
}: {
  rows: TimelineRow[];
  onLogInteraction: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={onLogInteraction}>
          Log interaction
        </Button>
      </div>

      {rows.length === 0 ? (
        <NoneYet label="No history yet" />
      ) : (
        <ul className="space-y-3" data-testid="timeline-list">
          {rows.map((row, i) => (
            <li
              key={`${row.kind}-${row.ref_id ?? i}-${row.at}`}
              data-testid="timeline-row"
              data-timeline-kind={row.kind}
              className="flex items-start gap-3 rounded-md border border-border bg-bg-muted/30 p-3"
            >
              <span className="text-xl leading-none" aria-hidden="true">
                {timelineIcon(row.kind)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-text-primary">{row.label}</p>
                <p className="text-xs text-text-muted">{relativeTime(row.at)}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---- Association sections ---------------------------------------------------

function NoneYet({ label }: { label: string }) {
  return <p className="py-6 text-center text-sm text-text-muted">{label}</p>;
}

function AssocTable({
  headers,
  children,
}: {
  headers: string[];
  children: ReactNode;
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-bg-muted/50 text-left">
            {headers.map((h) => (
              <th key={h} className="px-3 py-2 font-medium text-text-secondary">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function InquiriesTable({ inquiries }: { inquiries: OverviewInquiry[] }) {
  if (inquiries.length === 0) return <NoneYet label="None yet" />;
  return (
    <AssocTable headers={["Event date", "Guests", "Stage", "Created"]}>
      {inquiries.map((inq) => (
        <tr key={inq.id} className="border-b border-border last:border-0">
          <td className="px-3 py-2">
            <Link to="/pipeline" className="text-primary hover:underline">
              {formatDateOnly(inq.event_date)}
            </Link>
          </td>
          <td className="px-3 py-2 text-text-secondary">{inq.guest_count ?? "—"}</td>
          <td className="px-3 py-2">
            <Badge variant="secondary" size="sm">
              {STAGE_LABELS[inq.stage]}
            </Badge>
          </td>
          <td className="px-3 py-2 text-text-muted">{formatDateOnly(inq.created_at)}</td>
        </tr>
      ))}
    </AssocTable>
  );
}

function QuotesTable({ quotes }: { quotes: OverviewQuote[] }) {
  if (quotes.length === 0) return <NoneYet label="None yet" />;
  return (
    <AssocTable headers={["Package", "Total", "Status", "Created"]}>
      {quotes.map((q) => (
        <tr key={q.id} className="border-b border-border last:border-0">
          <td className="px-3 py-2">
            <Link to={`/quotes/${q.id}`} className="text-primary hover:underline">
              {q.package_code ? PACKAGE_CODE_LABELS[q.package_code] : "Quote"}
            </Link>
          </td>
          <td className="px-3 py-2 text-text-secondary">{centsToUsd(q.total_cents)}</td>
          <td className="px-3 py-2">
            <Badge variant={QUOTE_STATUS_BADGE_VARIANT[q.status]} size="sm">
              {QUOTE_STATUS_LABELS[q.status]}
            </Badge>
          </td>
          <td className="px-3 py-2 text-text-muted">{formatDateOnly(q.created_at)}</td>
        </tr>
      ))}
    </AssocTable>
  );
}

function EventsTable({ events }: { events: OverviewEvent[] }) {
  if (events.length === 0) return <NoneYet label="None yet" />;
  return (
    <AssocTable headers={["Date", "Venue", "Guests", "Status"]}>
      {events.map((ev) => (
        <tr key={ev.id} className="border-b border-border last:border-0">
          <td className="px-3 py-2">
            <Link to={`/events/${ev.id}`} className="text-primary hover:underline">
              {formatDateOnly(ev.event_date)}
            </Link>
          </td>
          <td className="px-3 py-2 text-text-secondary">{ev.venue_name ?? "—"}</td>
          <td className="px-3 py-2 text-text-secondary">{ev.guest_count ?? "—"}</td>
          <td className="px-3 py-2">
            <Badge variant={EVENT_STATUS_BADGE_VARIANT[ev.status]} size="sm">
              {EVENT_STATUS_LABELS[ev.status]}
            </Badge>
          </td>
        </tr>
      ))}
    </AssocTable>
  );
}

function InvoicesPaymentsSection({
  invoices,
  payments,
}: {
  invoices: OverviewInvoice[];
  payments: OverviewPayment[];
}) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-sm font-semibold text-text-primary">Invoices</h3>
        {invoices.length === 0 ? (
          <NoneYet label="None yet" />
        ) : (
          <AssocTable headers={["Number", "Kind", "Total", "Paid", "Status"]}>
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-b border-border last:border-0">
                <td className="px-3 py-2">
                  <Link to={`/money/${inv.id}`} className="text-primary hover:underline">
                    {inv.number ?? inv.id.slice(0, 8)}
                  </Link>
                </td>
                <td className="px-3 py-2">
                  <Badge variant={INVOICE_KIND_BADGE_VARIANT[inv.kind]} size="sm">
                    {INVOICE_KIND_LABELS[inv.kind]}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-text-secondary">{centsToUsd(inv.total_cents)}</td>
                <td className="px-3 py-2 text-text-secondary">
                  {centsToUsd(inv.paid_cents ?? 0)}
                </td>
                <td className="px-3 py-2">
                  <Badge variant={INVOICE_STATUS_BADGE_VARIANT[inv.status]} size="sm">
                    {INVOICE_STATUS_LABELS[inv.status]}
                  </Badge>
                </td>
              </tr>
            ))}
          </AssocTable>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-text-primary">Payments</h3>
        {payments.length === 0 ? (
          <NoneYet label="None yet" />
        ) : (
          <AssocTable headers={["Received", "Method", "Amount", "Invoice"]}>
            {payments.map((p) => (
              <tr key={p.id} className="border-b border-border last:border-0">
                <td className="px-3 py-2 text-text-secondary">{formatDateTime(p.received_at)}</td>
                <td className="px-3 py-2 text-text-secondary">
                  {PAYMENT_METHOD_LABELS[p.method]}
                </td>
                <td className="px-3 py-2 text-text-secondary">{centsToUsd(p.amount_cents)}</td>
                <td className="px-3 py-2">
                  {p.invoice_id ? (
                    <Link to={`/money/${p.invoice_id}`} className="text-primary hover:underline">
                      View invoice
                    </Link>
                  ) : (
                    <span className="text-text-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </AssocTable>
        )}
      </div>
    </div>
  );
}

function ReviewsTable({ reviews }: { reviews: OverviewReview[] }) {
  if (reviews.length === 0) return <NoneYet label="None yet" />;
  return (
    <AssocTable headers={["Platform", "Rating", "Received", "Link"]}>
      {reviews.map((r) => (
        <tr key={r.id} className="border-b border-border last:border-0">
          <td className="px-3 py-2">
            <Badge variant={REVIEW_PLATFORM_BADGE_VARIANT[r.platform]} size="sm">
              {REVIEW_PLATFORM_LABELS[r.platform]}
            </Badge>
          </td>
          <td className="px-3 py-2 text-text-secondary">{r.rating ? `${r.rating} ★` : "—"}</td>
          <td className="px-3 py-2 text-text-muted">{formatDateOnly(r.received_at)}</td>
          <td className="px-3 py-2">
            {r.url ? (
              <a href={r.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                View
              </a>
            ) : (
              <Link to="/reviews" className="text-primary hover:underline">
                View
              </Link>
            )}
          </td>
        </tr>
      ))}
    </AssocTable>
  );
}

function OutreachSection({ outreach }: { outreach: OverviewOutreach[] }) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-text-primary">Outreach</h3>
      {outreach.length === 0 ? (
        <NoneYet label="None yet" />
      ) : (
        <AssocTable headers={["Tier", "Status", "Verified", "Next touch"]}>
          {outreach.map((o) => (
            <tr key={o.id} className="border-b border-border last:border-0">
              <td className="px-3 py-2">
                <Link to="/outreach" className="text-primary hover:underline">
                  <Badge variant={TIER_BADGE_VARIANT[o.tier]} size="sm">
                    {TIER_LABELS[o.tier]}
                  </Badge>
                </Link>
              </td>
              <td className="px-3 py-2 text-text-secondary">{OUTREACH_STATUS_LABELS[o.status]}</td>
              <td className="px-3 py-2">
                {o.verified ? (
                  <Badge variant="success" size="sm">Verified</Badge>
                ) : (
                  <Badge variant="warning" size="sm">⚠ verify</Badge>
                )}
              </td>
              <td className="px-3 py-2 text-text-muted">{formatDateOnly(o.next_touch_at)}</td>
            </tr>
          ))}
        </AssocTable>
      )}
    </div>
  );
}
